import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { addOutline, bugOutline, bulbOutline, chatbubbleEllipsesOutline, chevronBackOutline, chevronForwardOutline, sendOutline } from 'ionicons/icons';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { TicketImageViewerComponent, TicketImageViewerItem } from '../../components/ticket-image-viewer/ticket-image-viewer.component';
import { FormFeedbackService } from '../../services/form-feedback.service';
import { ImageOptimizationService } from '../../services/image-optimization.service';
import { AnalyticsService } from '../../services/analytics.service';
import {
  SupportApiError,
  SupportService,
  SupportTicket,
  SupportTicketDetail,
  SupportTicketAttachment,
  SupportTicketType,
} from '../../services/support.service';
import { environment } from '../../../environments/environment';

type WorkspaceMode = 'compose' | 'detail';

interface TicketTypeOption {
  type: SupportTicketType;
  title: string;
  description: string;
  icon: string;
}

interface ImageDraft {
  file: File;
  previewUrl: string;
}

@Component({
  selector: 'app-feedback-page',
  standalone: true,
  imports: [DatePipe, FormsModule, IonIcon, ApiLoadErrorComponent, InfoTooltipComponent, TicketImageViewerComponent],
  templateUrl: './feedback-page.component.html',
  styleUrls: ['./feedback-page.component.scss']
})
export class FeedbackPageComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly ticketTypes: TicketTypeOption[] = [
    { type: 'bug', title: 'Report a bug', description: 'Something did not work as expected.', icon: 'bug-outline' },
    { type: 'feedback', title: 'Share feedback', description: 'Tell us what felt useful or confusing.', icon: 'chatbubble-ellipses-outline' },
    { type: 'feature', title: 'Request a feature', description: 'Describe a capability that would help.', icon: 'bulb-outline' },
  ];

  tickets: SupportTicket[] = [];
  selected: SupportTicketDetail | null = null;
  mode: WorkspaceMode = 'compose';
  isLoading = true;
  isLoadingDetail = false;
  isSubmitting = false;
  isReplying = false;
  isPreparingImages = false;
  isUploadingImages = false;
  loadError = '';
  formError = '';
  formNotice = '';
  reply = '';
  draft = this.emptyDraft();
  ticketImages: ImageDraft[] = [];
  imageError = '';
  isImageViewerOpen = false;
  imageViewerStartIndex = 0;
  readonly attachmentPreviewUrls: Record<string, string> = {};
  private ticketLoadSubscription: Subscription | null = null;
  @ViewChild('ticketRail') private ticketRail?: ElementRef<HTMLElement>;

  canScrollTicketsLeft = false;
  canScrollTicketsRight = false;
  isTicketRailDragging = false;
  private ticketRailPointerId: number | null = null;
  private ticketRailStartX = 0;
  private ticketRailStartScrollLeft = 0;
  private ticketRailDragMoved = false;
  private suppressTicketRailClick = false;

  constructor(
    private readonly supportService: SupportService,
    private readonly formFeedbackService: FormFeedbackService,
    private readonly imageOptimizationService: ImageOptimizationService,
    private readonly analyticsService: AnalyticsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {
    addIcons({ addOutline, bugOutline, bulbOutline, chatbubbleEllipsesOutline, chevronBackOutline, chevronForwardOutline, sendOutline });
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => this.loadTickets(params.get('ticket')));
  }

  ngOnDestroy(): void {
    this.ticketLoadSubscription?.unsubscribe();
    this.clearTicketImages();
    this.clearAttachmentPreviews();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleTicketRailUpdate();
  }

  get isDraftValid(): boolean {
    return this.draft.title.trim().length >= 3 && this.draft.message.trim().length >= 10;
  }

  get selectedTicket(): SupportTicket | null {
    return this.selected?.ticket ?? null;
  }

  get selectedAttachments(): SupportTicketAttachment[] {
    return this.selected?.attachments ?? [];
  }

  get ticketImageViewerImages(): TicketImageViewerItem[] {
    const attachments = this.selectedAttachments.flatMap((attachment) => {
      const src = this.attachmentPreview(attachment);
      return src ? [{
        id: `attachment:${attachment.publicId}`,
        src,
        alt: attachment.fileName,
        label: `${attachment.fileName} - ${this.formatFileSize(attachment.sizeBytes)}`,
      }] : [];
    });
    const drafts = this.ticketImages.map((image) => ({
      id: `draft:${image.previewUrl}`,
      src: image.previewUrl,
      alt: image.file.name,
      label: `${image.file.name} - ${this.formatFileSize(image.file.size)}`,
    }));
    return [...attachments, ...drafts];
  }

  get typeOption(): TicketTypeOption {
    return this.ticketTypes.find((option) => option.type === this.draft.type) ?? this.ticketTypes[0];
  }

  get selectedTicketLabel(): string {
    return this.selectedTicket ? this.typeLabel(this.selectedTicket.type) : '';
  }

  loadTickets(ticketId: string | null = null): void {
    this.ticketLoadSubscription?.unsubscribe();
    this.isLoading = true;
    this.loadError = '';
    this.ticketLoadSubscription = this.supportService.listMyTickets().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (tickets) => {
        this.runInAngular(() => {
          this.ticketLoadSubscription = null;
          this.tickets = tickets;
          this.isLoading = false;
          this.scheduleTicketRailUpdate();
          if (ticketId) {
            const ticket = tickets.find((entry) => entry.publicId === ticketId);
            if (ticket) {
              this.openTicket(ticket, false);
            }
          }
        });
      },
      error: (error) => {
        this.runInAngular(() => {
          this.ticketLoadSubscription = null;
          this.loadError = this.errorMessage(error, 'Could not load your feedback conversations.');
          this.isLoading = false;
        });
      }
    });
  }

  stopLoadingTickets(): void {
    if (!this.isLoading) {
      return;
    }

    this.ticketLoadSubscription?.unsubscribe();
    this.ticketLoadSubscription = null;
    this.isLoading = false;
    this.loadError = 'Loading conversations was stopped. You can retry when the API is available.';
  }

  selectType(type: SupportTicketType): void {
    this.draft.type = type;
  }

  startNewTicket(): void {
    this.mode = 'compose';
    this.selected = null;
    this.formError = '';
    this.formNotice = '';
    this.reply = '';
    this.clearTicketImages();
    this.imageError = '';
    this.draft = this.emptyDraft();
    void this.router.navigate([], { relativeTo: this.route, queryParams: { ticket: null }, queryParamsHandling: 'merge' });
  }

  submitTicket(form: HTMLFormElement): void {
    if (this.isSubmitting) {
      return;
    }

    if (!this.isDraftValid) {
      this.formFeedbackService.showInvalidFields(form, this.invalidDraftFields());
      return;
    }

    this.isSubmitting = true;
    this.formError = '';
    this.formNotice = '';
    this.supportService.createTicket({
      type: this.draft.type,
      title: this.draft.title.trim(),
      message: this.draft.message.trim(),
      context: this.draft.type === 'bug' ? this.buildBugContext() : null,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (ticket) => {
        this.runInAngular(() => {
          this.isSubmitting = false;
          this.upsertTicket(ticket);
          this.draft = this.emptyDraft();
          this.formNotice = this.ticketImages.length
            ? 'Your message was sent. Optimized images are uploading now.'
            : 'Your message reached the BizzWeaver team. We will reply here and by email.';
          this.openTicket(ticket);
          this.uploadPendingImages(ticket);
        });
      },
      error: (error) => {
        this.runInAngular(() => {
          this.formError = this.errorMessage(error, 'Your message could not be sent right now.');
          this.isSubmitting = false;
        });
      }
    });
  }

  openTicket(ticket: SupportTicket, updateUrl = true): void {
    this.mode = 'detail';
    this.isLoadingDetail = true;
    this.formError = '';
    this.supportService.getMyTicket(ticket.publicId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (detail) => {
        this.runInAngular(() => {
          this.selected = detail;
          this.isLoadingDetail = false;
          this.upsertTicket(detail.ticket);
          this.loadAttachmentPreviews(detail.ticket, detail.attachments);
          if (updateUrl) {
            void this.router.navigate([], { relativeTo: this.route, queryParams: { ticket: ticket.publicId }, queryParamsHandling: 'merge' });
          }
        });
      },
      error: (error) => {
        this.runInAngular(() => {
          this.formError = this.errorMessage(error, 'This ticket could not be opened.');
          this.isLoadingDetail = false;
        });
      }
    });
  }

  openTicketFromRail(ticket: SupportTicket, event: MouseEvent): void {
    if (this.suppressTicketRailClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.openTicket(ticket);
  }

  scrollTicketRail(direction: -1 | 1): void {
    const rail = this.ticketRail?.nativeElement;
    if (!rail || rail.scrollWidth <= rail.clientWidth + 2) {
      return;
    }

    rail.scrollBy({ left: direction * Math.max(rail.clientWidth * 0.72, 280), behavior: 'smooth' });
  }

  updateTicketRailControls(): void {
    const rail = this.ticketRail?.nativeElement;
    if (!rail) {
      this.canScrollTicketsLeft = false;
      this.canScrollTicketsRight = false;
      return;
    }

    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    this.canScrollTicketsLeft = rail.scrollLeft > 2;
    this.canScrollTicketsRight = rail.scrollLeft < maxScrollLeft - 2;
  }

  startTicketRailDrag(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    const rail = this.ticketRail?.nativeElement;
    if (!rail) {
      return;
    }

    this.ticketRailPointerId = event.pointerId;
    this.ticketRailStartX = event.clientX;
    this.ticketRailStartScrollLeft = rail.scrollLeft;
    this.ticketRailDragMoved = false;
    rail.setPointerCapture(event.pointerId);
  }

  moveTicketRailDrag(event: PointerEvent): void {
    if (this.ticketRailPointerId !== event.pointerId) {
      return;
    }

    const rail = this.ticketRail?.nativeElement;
    if (!rail) {
      return;
    }

    const deltaX = event.clientX - this.ticketRailStartX;
    if (!this.ticketRailDragMoved && Math.abs(deltaX) > 4) {
      this.ticketRailDragMoved = true;
      this.isTicketRailDragging = true;
    }

    if (this.ticketRailDragMoved) {
      rail.scrollLeft = this.ticketRailStartScrollLeft - deltaX;
    }
  }

  endTicketRailDrag(event: PointerEvent): void {
    if (this.ticketRailPointerId !== event.pointerId) {
      return;
    }

    const rail = this.ticketRail?.nativeElement;
    if (rail?.hasPointerCapture(event.pointerId)) {
      rail.releasePointerCapture(event.pointerId);
    }

    this.ticketRailPointerId = null;
    this.isTicketRailDragging = false;
    if (this.ticketRailDragMoved) {
      this.suppressTicketRailClick = true;
      window.setTimeout(() => {
        this.suppressTicketRailClick = false;
      });
    }
    this.ticketRailDragMoved = false;
    this.updateTicketRailControls();
  }

  sendReply(): void {
    if (!this.selectedTicket || !this.reply.trim() || this.isReplying) {
      return;
    }

    this.isReplying = true;
    this.formError = '';
    this.supportService.replyToMyTicket(this.selectedTicket.publicId, this.reply.trim()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (detail) => {
        this.runInAngular(() => {
          this.selected = detail;
          this.upsertTicket(detail.ticket);
          this.loadAttachmentPreviews(detail.ticket, detail.attachments);
          this.reply = '';
          this.isReplying = false;
        });
      },
      error: (error) => {
        this.runInAngular(() => {
          this.formError = this.errorMessage(error, 'Your reply could not be sent right now.');
          this.isReplying = false;
        });
      }
    });
  }

  typeLabel(type: SupportTicketType): string {
    return type === 'bug' ? 'Bug report' : type === 'feature' ? 'Feature request' : 'Feedback';
  }

  statusLabel(status: SupportTicket['status']): string {
    return status === 'in_progress' ? 'In progress' : status === 'completed' ? 'Completed' : status === 'planned' ? 'Planned' : status[0].toUpperCase() + status.slice(1);
  }

  statusClass(status: SupportTicket['status']): string {
    return `is-${status.replace('_', '-')}`;
  }

  async addTicketImages(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) {
      return;
    }

    const available = this.imageOptimizationService.maxSupportImages - this.ticketImages.length - (this.selected?.attachments.length ?? 0);
    if (available <= 0) {
      this.imageError = `A ticket can include up to ${this.imageOptimizationService.maxSupportImages} images.`;
      return;
    }

    this.isPreparingImages = true;
    this.imageError = files.length > available
      ? `Only the first ${available} image${available === 1 ? '' : 's'} will be added.`
      : '';

    const prepared: ImageDraft[] = [];
    try {
      for (const file of files.slice(0, available)) {
        const optimized = await this.imageOptimizationService.optimizeSupportImage(file);
        if (this.currentImageBytes() + prepared.reduce((total, image) => total + image.file.size, 0) + optimized.size > this.imageOptimizationService.maxSupportImageTotalBytes) {
          this.imageError = 'Images on one ticket cannot exceed 6 MB in total.';
          break;
        }
        prepared.push({ file: optimized, previewUrl: URL.createObjectURL(optimized) });
      }
    } catch (error) {
      prepared.forEach((image) => URL.revokeObjectURL(image.previewUrl));
      this.runInAngular(() => {
        this.imageError = error instanceof Error ? error.message : 'An image could not be optimized.';
        this.isPreparingImages = false;
      });
      return;
    }

    this.runInAngular(() => {
      this.ticketImages = [...this.ticketImages, ...prepared];
      this.isPreparingImages = false;
    });
  }

  removeTicketImage(image: ImageDraft): void {
    URL.revokeObjectURL(image.previewUrl);
    this.ticketImages = this.ticketImages.filter((entry) => entry !== image);
    this.imageError = '';
  }

  openTicketImageViewer(imageId: string): void {
    const index = this.ticketImageViewerImages.findIndex((image) => image.id === imageId);
    if (index < 0) {
      return;
    }
    this.imageViewerStartIndex = index;
    this.isImageViewerOpen = true;
  }

  closeTicketImageViewer(): void {
    this.isImageViewerOpen = false;
  }

  attachmentPreview(attachment: SupportTicketAttachment): string | null {
    return this.attachmentPreviewUrls[attachment.publicId] ?? null;
  }

  formatFileSize(sizeBytes: number): string {
    return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
  }

  private emptyDraft(): { type: SupportTicketType; title: string; message: string } {
    return { type: 'bug', title: '', message: '' };
  }

  private invalidDraftFields(): string[] {
    return [
      this.draft.title.trim().length >= 3 ? null : 'feedback_title',
      this.draft.message.trim().length >= 10 ? null : 'feedback_message',
    ].filter((name): name is string => name !== null);
  }

  private uploadPendingImages(ticket: SupportTicket): void {
    if (!this.ticketImages.length || this.isUploadingImages) {
      return;
    }

    const image = this.ticketImages[0];
    this.isUploadingImages = true;
    this.supportService.uploadMyTicketAttachment(ticket.publicId, image.file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.runInAngular(() => {
            this.removeTicketImage(image);
            this.isUploadingImages = false;
            if (this.ticketImages.length) {
              this.uploadPendingImages(ticket);
              return;
            }
            this.formNotice = 'Your message and optimized images reached the BizzWeaver team.';
            this.openTicket(ticket, false);
          });
        },
        error: (error) => {
          this.runInAngular(() => {
            this.isUploadingImages = false;
            this.imageError = `${this.errorMessage(error, 'The image could not be uploaded.')} Keep this page open and try again.`;
            this.formNotice = 'Your message was sent, but one or more images still need uploading.';
          });
        }
      });
  }

  retryImageUpload(): void {
    if (this.selectedTicket) {
      this.uploadPendingImages(this.selectedTicket);
    }
  }

  private loadAttachmentPreviews(
    ticket: SupportTicket,
    attachments: readonly SupportTicketAttachment[],
    clearExisting = true
  ): void {
    if (clearExisting) {
      this.clearAttachmentPreviews();
    }
    const pending = attachments.filter((attachment) => !this.attachmentPreviewUrls[attachment.publicId]);
    for (const attachment of pending) {
      this.supportService.getMyTicketAttachment(ticket.publicId, attachment.publicId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (blob) => this.runInAngular(() => {
            if (this.selectedTicket?.publicId !== ticket.publicId) {
              return;
            }
            this.attachmentPreviewUrls[attachment.publicId] = URL.createObjectURL(blob);
          }),
          error: () => {
            // The ticket remains usable if an individual image has expired or was removed.
          }
        });
    }
  }

  private currentImageBytes(): number {
    const attached = this.selected?.attachments.reduce((total, attachment) => total + attachment.sizeBytes, 0) ?? 0;
    return attached + this.ticketImages.reduce((total, image) => total + image.file.size, 0);
  }

  private clearTicketImages(): void {
    this.isImageViewerOpen = false;
    this.ticketImages.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    this.ticketImages = [];
  }

  private clearAttachmentPreviews(): void {
    this.isImageViewerOpen = false;
    Object.values(this.attachmentPreviewUrls).forEach((url) => URL.revokeObjectURL(url));
    Object.keys(this.attachmentPreviewUrls).forEach((key) => delete this.attachmentPreviewUrls[key]);
  }

  private upsertTicket(ticket: SupportTicket): void {
    this.tickets = [ticket, ...this.tickets.filter((entry) => entry.publicId !== ticket.publicId)]
      .sort((left, right) => right.lastActivityAt.localeCompare(left.lastActivityAt));
    this.scheduleTicketRailUpdate();
  }

  private scheduleTicketRailUpdate(): void {
    requestAnimationFrame(() => this.runInAngular(() => this.updateTicketRailControls()));
  }

  private buildBugContext() {
    return {
      analyticsSessionId: this.analyticsService.currentSessionId ?? undefined,
      environment: environment.envName,
      appVersion: 'web',
    };
  }

  private errorMessage(error: SupportApiError, fallback: string): string {
    return error?.message || fallback;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
